import { Email, EmailGroup, EmailCategory, EmailAttachment } from '@/types/email';
import { Subscription } from '@/types/subscription';
import { ReactNode } from 'react';

export interface EmailListProps {
  onBatchComplete: (cleanedCount: number, isNewBatch?: boolean, failedOps?: number) => void;
}

export interface ProcessingStatus {
  action: 'delete' | 'archive' | 'unsubscribe' | null;
  total: number;
  processed: number;
}

export interface EmailAction {
  type: 'archive' | 'delete';
  success: boolean;
}

export interface SelectedEmail extends Email {
  content?: string;
  mimeType?: string;
  headers?: Record<string, string>;
  attachments?: EmailAttachment[];
}

export interface CategoryLabel {
  label: string;
  icon: ReactNode;
  actionLabel: string;
  description: string;
}

export type CategoryLabels = {
  [K in EmailCategory]: CategoryLabel;
};

export interface SenderActionModal {
  senders: Array<{
    sender: string;
    senderKey: string;
    emails: Email[];
  }>;
  currentIndex: number;
}

export interface SubscriptionStatus {
  isActive: boolean;
  subscription: Subscription | null;
}

export type UnsubscribeMethod = 'none' | 'link' | 'post' | 'list-unsubscribe';
export type UnsubscribeAction = 'local' | 'url' | 'form' | 'email' | 'header';
export type SenderUnsubscribeTier = 'gmail_one_click' | 'best_effort' | 'winbox_only';
export type SenderUnsubscribeOutcome = 'one_click_requested' | 'request_sent' | 'blocked_only' | 'request_failed';

export interface UnsubscribeValue {
  unsubscribeLink?: string;
  method: UnsubscribeMethod;
  action: UnsubscribeAction;
  formData?: Record<string, string>;
  headers?: Record<string, string>;
  listUnsubscribeHeader?: string;
  listUnsubscribePost?: string;
  senderUnsubscribeTier?: SenderUnsubscribeTier;
}

// Column sorting options
export type SortOption =
  | 'date_desc'
  | 'date_asc'
  | 'priority_desc'
  | 'priority_asc'
  | 'sender_asc'
  | 'sender_desc'
  | 'unread_first'
  | 'attachments_first';

export type ColumnSortState = Record<EmailCategory, SortOption>;

export const SORT_OPTIONS: Array<{ value: SortOption; label: string }> = [
  { value: 'date_desc', label: 'Date (Newest)' },
  { value: 'date_asc', label: 'Date (Oldest)' },
  { value: 'priority_desc', label: 'Priority (High→Low)' },
  { value: 'priority_asc', label: 'Priority (Low→High)' },
  { value: 'sender_asc', label: 'Sender (A→Z)' },
  { value: 'sender_desc', label: 'Sender (Z→A)' },
  { value: 'unread_first', label: 'Unread First' },
  { value: 'attachments_first', label: 'Has Attachments' },
];

export const DEFAULT_COLUMN_SORTS: ColumnSortState = {
  'do_nothing': 'date_desc',
  'delete': 'date_desc',
  'archive': 'date_desc',
  'unsubscribe': 'date_desc',
};
